import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.ts'

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

export interface TooltipProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /** Text or node rendered inside the tooltip bubble. */
  content: ReactNode
  /** Side of the trigger to position the bubble on. */
  side?: TooltipSide
  /**
   * Force `tabIndex={0}` on the wrapper so keyboard users can surface the
   * tooltip even when the wrapped element wouldn't itself receive focus.
   * Auto-detected when the wrapped element has `disabled` (button/input) —
   * pass explicitly only to override.
   */
  focusable?: boolean
}

interface Coords {
  top: number
  left: number
}

const OFFSET = 8

const sideTransform: Record<TooltipSide, string> = {
  top: '-translate-x-1/2 -translate-y-full',
  bottom: '-translate-x-1/2',
  left: '-translate-y-1/2 -translate-x-full',
  right: '-translate-y-1/2',
}

/**
 * Pure-React tooltip with portal rendering.
 *
 * Bubble lives at the `document.body` level via `createPortal`, so it never
 * gets clipped by ancestor `overflow: hidden` / `overflow-y-auto` containers
 * (scrollable Card bodies, table viewports). Position is measured at runtime
 * from the trigger's `getBoundingClientRect()` and recomputed on resize;
 * scroll on any ancestor closes the tooltip (cheaper than re-anchoring and
 * matches mouse-hover semantics — moving away cancels intent).
 *
 * ARIA: when open, the trigger child is cloned with `aria-describedby={id}`
 * so screen readers announce the description against the actual focused
 * element rather than its wrapper. Disabled triggers swallow focus, so we
 * additionally put `tabIndex={0}` on the wrapper — auto-detected from
 * `child.props.disabled` or forced via the `focusable` prop — letting
 * keyboard users tab to the wrapper and still surface the bubble.
 */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  { content, side = 'top', focusable, className, children, ...props },
  ref,
) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<Coords | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const id = useId()

  const child = isValidElement(children) ? Children.only(children) : null
  const childIsDisabled =
    child !== null &&
    typeof child.props === 'object' &&
    child.props !== null &&
    (child.props as { disabled?: unknown }).disabled === true
  const needsWrapperFocus = focusable ?? childIsDisabled

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const measure = () => {
      const node = wrapperRef.current
      if (!node) return
      setCoords(computeCoords(node.getBoundingClientRect(), side))
    }
    measure()
    const onScroll = () => setOpen(false)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', measure)
    }
  }, [open, side])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const triggerNode =
    child !== null && open
      ? cloneElement(child as ReactElement<{ 'aria-describedby'?: string }>, {
          'aria-describedby': id,
        })
      : children

  const bubbleStyle: CSSProperties =
    coords !== null
      ? { position: 'fixed', top: coords.top, left: coords.left }
      : { position: 'fixed', visibility: 'hidden' }

  return (
    <div
      ref={(node) => {
        wrapperRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as { current: HTMLDivElement | null }).current = node
      }}
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={needsWrapperFocus ? 0 : undefined}
      {...props}
    >
      {triggerNode}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            style={bubbleStyle}
            className={cn(
              'pointer-events-none z-50 max-w-xs whitespace-nowrap rounded-md border border-border bg-background-secondary px-2 py-1 text-xs text-foreground shadow-lg',
              sideTransform[side],
            )}
          >
            {content}
          </span>,
          document.body,
        )}
    </div>
  )
})

function computeCoords(rect: DOMRect, side: TooltipSide): Coords {
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  switch (side) {
    case 'top':
      return { top: rect.top - OFFSET, left: cx }
    case 'bottom':
      return { top: rect.bottom + OFFSET, left: cx }
    case 'left':
      return { top: cy, left: rect.left - OFFSET }
    case 'right':
      return { top: cy, left: rect.right + OFFSET }
  }
}
